"use client";
import useClickOutside from "@/hooks/use-click-outside";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useRef } from "react";
import CloseBtTn from "./close-button";
import Divider from "./divider";
import Trophy from "./trophy";

type CardProps = {
  title: string;
  description: string;
  isExpanded: boolean;
  onClick: () => void;
  onClose: () => void;
  index: number;
  isLayoutExpanded: boolean;
  cardWidth?: string;
  lang: Lang;
  hoveredIndex: number | null;
  onHover: () => void;
  onHoverEnd: () => void;
};

export function ExpandableCard({
  title,
  description,
  isExpanded,
  onClick,
  onClose,
  index,
  isLayoutExpanded,
  lang,
  hoveredIndex,
  onHover,
  onHoverEnd,
}: CardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(cardRef, () => onClose);

  const cardVariants: Variants = {
    initial: {
      position: "relative",
      zIndex: 1,
      // width: cardWidth,
    },
    expanded: {
      position: "absolute",
      top: 0,
      width: "700px",
      zIndex: 10,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        ease: "easeInOut",
      },
    },
  };

  const descriptionVariants: Variants = {
    hidden: {
      opacity: 0,
      x: -10,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        delay: 0.3,
      },
    },
  };

  const maxWidth = 700;

  const leftOffset = index > 2 ? `calc(100% - ${maxWidth}px)` : 0;

  return (
    <div ref={cardRef} className="relative">
      <motion.div
        onHoverStart={() => onHover?.()}
        onHoverEnd={() => onHoverEnd?.()}
        onClick={onClick}
        variants={cardVariants}
        initial="initial"
        animate={isExpanded ? "expanded" : "initial"}
        className={cn(
          "cursor-pointer overflow-hidden rounded-[20px] border bg-foreground transition-all duration-150 ease-linear",
          isExpanded
            ? "z-50 border-secondary"
            : "border-secondary/10 hover:scale-105 hover:border-secondary",
        )}
        layout
        style={{
          right: lang === "ar" && isExpanded ? leftOffset : 0,
          left: lang === "en" && isExpanded ? leftOffset : 0,
          maxWidth: isExpanded ? `${maxWidth}px` : "300px",
        }}
      >
        <div
          className={cn(
            "flex h-[200px] items-center justify-center px-10",
            isExpanded ? "text-secondary" : "text-primary",
            isLayoutExpanded ? "text-secondary" : "text-primary",
            isLayoutExpanded && !isExpanded ? "opacity-30" : "",
          )}
        >
          <div className="flex items-center gap-8">
            <span className="flex-shrink-0">
              <Trophy
                isExpanded={
                  isExpanded ||
                  (hoveredIndex !== null && hoveredIndex === index)
                }
              />
            </span>
            <p
              className={cn(
                "w-[120px] 2xl:text-xl ltr:font-playfair",
                hoveredIndex !== null && hoveredIndex === index
                  ? "text-secondary"
                  : "",
              )}
            >
              {title}
            </p>

            <AnimatePresence mode="wait" initial={false}>
              {isExpanded && (
                <motion.div
                  variants={descriptionVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="flex w-[400px] items-center gap-8"
                >
                  <Divider />
                  {description}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {isExpanded && (
          <motion.button
            className="absolute top-2 rounded-full bg-white p-2 shadow ltr:right-2 rtl:left-2"
            onClick={(e) => {
              e.stopPropagation();

              onClose();
            }}
          >
            <CloseBtTn />
          </motion.button>
        )}
      </motion.div>
    </div>
  );
}
